import { useState, useEffect, useCallback } from 'react'
import { localStorageGet, localStorageSet } from '@/lib/storage'

const FAVORITES_STORAGE_KEY = 'qr-menu-favorites'

// ============================================================================
// Hook: useFavorites
// ============================================================================

export interface UseFavoritesReturn {
  favorites: Set<string>
  isFavorite: (itemId: string) => boolean
  toggleFavorite: (itemId: string) => void
  favoriteCount: number
}

export function useFavorites(): UseFavoritesReturn {
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    try {
      const stored = localStorageGet<string[]>(FAVORITES_STORAGE_KEY, [])
      return new Set(stored)
    } catch (error) {
      console.error('Failed to load favorites:', error)
      return new Set()
    }
  })

  // Save to localStorage whenever favorites change
  useEffect(() => {
    try {
      localStorageSet(FAVORITES_STORAGE_KEY, Array.from(favorites))
    } catch (error) {
      console.error('Failed to save favorites:', error)
    }
  }, [favorites])

  const isFavorite = useCallback(
    (itemId: string) => {
      return favorites.has(itemId)
    },
    [favorites]
  )

  const toggleFavorite = useCallback(
    (itemId: string) => {
      setFavorites((prev) => {
        const newFavorites = new Set(prev)
        if (newFavorites.has(itemId)) {
          newFavorites.delete(itemId)
        } else {
          newFavorites.add(itemId)
        }
        return newFavorites
      })
    },
    []
  )

  return {
    favorites,
    isFavorite,
    toggleFavorite,
    favoriteCount: favorites.size,
  }
}
